import React from 'react';

import { css } from '@emotion/css';
import { schemas } from '@recurrency/core-api-schema';
import { AsyncJobDTO, AsyncJobName, AsyncJobStatus } from '@recurrency/core-api-schema/dist/asyncJobs/common';
import { ColumnType } from 'antd/lib/table';
import { colors } from 'theme/colors';

import { AsyncTable } from 'components/AsyncTable';
import { useCoreApiTableProps } from 'components/AsyncTable/useAsyncTableProps';
import { Button } from 'components/Button';
import { Card, CardHeader, CardSection } from 'components/Card';
import { StackedBarChart } from 'components/Charts/StackedBarChart';
import { NoDataMessage } from 'components/NoDataMessage';

import { formatDate, formatDuration, formatNumber } from 'utils/formatting';
import { useHashState } from 'utils/routes';
import { asKeyOf, sortableNumberColumn } from 'utils/tables';

import { ExportJobsPageHashState } from 'types/hash-state';

import { JobStatusBadge } from '../JobStatusBadge';
import { AutomatedExportRunModal } from './AutomatedExportRunModal';
import { ItemSelectionCard } from './ItemSelectionCard';
import { LastRunCard } from './LastRunCard';
import { formatRunId, formatRunLabel, getBatchCounts, getLocalTimezoneAbbreviation } from './utils';

const RUNS_PER_PAGE = 20;
/** Two weeks of nightlies is enough to see a trend without the axis becoming unreadable. */
const RUNS_CHARTED = 14;

/** "Automated Exports" tab of the Export Jobs page: nightly min/max runs and what each one changed. */
export const AutomatedExportsTab = () => {
  const [hashState, updateHashState] = useHashState<ExportJobsPageHashState>();
  const timezone = getLocalTimezoneAbbreviation();

  const tableProps = useCoreApiTableProps({
    schema: schemas.asyncJobs.getAsyncJobs,
    queryParams: {
      filter: { jobNames: [AsyncJobName.AutomatedMinMaxUpdate] },
      limit: RUNS_PER_PAGE,
      offset: ((hashState.page || 1) - 1) * RUNS_PER_PAGE,
    },
  });

  // no auto refresh: these runs fire once a night on a schedule, so there is nothing to watch land
  const runs = tableProps.items;

  // the list is newest first; the chart reads left to right oldest to newest
  const chartData = [...runs]
    .slice(0, RUNS_CHARTED)
    .reverse()
    .map((run) => {
      const counts = getBatchCounts(run);
      return { label: formatRunLabel(run.createdAt), Exported: counts.exported, Failed: counts.failed };
    });

  const columns: ColumnType<AsyncJobDTO>[] = [
    {
      title: 'Run ID',
      dataIndex: asKeyOf<AsyncJobDTO>('id'),
      render: (id: string) => (
        <Button type="link" onClick={() => updateHashState({ runId: id })} style={{ padding: 0 }}>
          {formatRunId(id)}
        </Button>
      ),
    },
    {
      title: 'Status',
      dataIndex: asKeyOf<AsyncJobDTO>('status'),
      width: 120,
      render: (status: AsyncJobStatus) => <JobStatusBadge status={status} />,
    },
    sortableNumberColumn({
      title: `Started (${timezone})`,
      dataIndex: asKeyOf<AsyncJobDTO>('createdAt'),
      render: (createdAt: string) => formatDate(createdAt, true, true),
      sorter: true,
      defaultSortOrder: 'descend',
    }),
    sortableNumberColumn({
      title: 'Duration',
      dataIndex: asKeyOf<AsyncJobDTO>('durationMs'),
      render: (durationMs: number) => formatDuration(durationMs),
      sorter: true,
    }),
    {
      title: 'Item-Locations',
      dataIndex: asKeyOf<AsyncJobDTO>('recordCount'),
      align: 'right',
      render: (recordCount: number) => formatNumber(recordCount ?? 0),
    },
    {
      title: 'Batches Exported',
      dataIndex: asKeyOf<AsyncJobDTO>('stepStatusCounts'),
      align: 'right',
      render: (_, record) => {
        const { exported } = getBatchCounts(record);
        return exported > 0 ? (
          <span
            className={css`
              color: ${colors.success[600]};
              font-weight: 600;
            `}
          >
            {exported}
          </span>
        ) : (
          0
        );
      },
    },
    {
      title: 'Batches Failed',
      dataIndex: asKeyOf<AsyncJobDTO>('stepStatusCounts'),
      align: 'right',
      render: (_, record) => {
        const { failed } = getBatchCounts(record);
        return failed > 0 ? (
          <span
            className={css`
              color: ${colors.danger[600]};
              font-weight: 600;
            `}
          >
            {failed}
          </span>
        ) : (
          0
        );
      },
    },
  ];

  return (
    <>
      <div
        className={css`
          display: grid;
          grid-template-columns: 3fr 2fr;
          gap: 16px;
          margin-bottom: 16px;
          @media (max-width: 1100px) {
            grid-template-columns: 1fr;
          }
        `}
      >
        <Card>
          <CardHeader title="Recent runs" description={`Batches per nightly run, times in ${timezone}`} />
          <CardSection>
            {tableProps.isLoading ? null : chartData.length === 0 ? (
              <NoDataMessage description="No automated exports have run yet." />
            ) : (
              <StackedBarChart
                data={chartData}
                bars={[
                  { key: 'Exported', color: colors.success[500], title: 'Exported' },
                  { key: 'Failed', color: colors.danger[500], title: 'Failed' },
                ]}
                valueFormatter={(value) => String(value)}
                // batches are whole, so a run of 3 must not draw ticks at 0.5, 1.5, 2.5
                allowDecimals={false}
                height={240}
              />
            )}
          </CardSection>
        </Card>
        <LastRunCard lastRun={runs[0]} onViewAll={(runId) => updateHashState({ runId })} />
      </div>
      <div
        className={css`
          margin-bottom: 16px;
        `}
      >
        <ItemSelectionCard />
      </div>
      <AsyncTable
        tableProps={{
          ...tableProps,
          page: hashState.page || 1,
          setPage: (pageNum) => updateHashState({ page: pageNum }),
        }}
        columns={columns}
      />
      {hashState.runId && (
        <AutomatedExportRunModal
          jobId={hashState.runId}
          runIds={runs.map((run) => run.id)}
          onNavigate={(runId) => updateHashState({ runId })}
          onClose={() => updateHashState({ runId: undefined })}
        />
      )}
    </>
  );
};
